/**
 * Formatting utilities for dates, numbers, and currencies
 * Provides consistent formatting across the application with i18n support
 */

import { useSettingsStore } from '@/store/settings.store'
import { config } from '@/core/config'

/**
 * Format date according to user preferences and locale
 */
export const formatDate = (
  date: string | Date,
  options?: {
    format?: 'short' | 'long' | 'time' | 'datetime'
    locale?: string
  }
): string => {
  const { language, timezone } = useSettingsStore.getState()
  const { format = 'short', locale = language } = options || {}

  const dateObj = typeof date === 'string' ? new Date(date) : date

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date'
  }

  try {
    switch (format) {
      case 'short':
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          timeZone: timezone,
        }).format(dateObj)

      case 'long':
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          timeZone: timezone,
        }).format(dateObj)

      case 'time':
        return new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          timeZone: timezone,
        }).format(dateObj)

      case 'datetime':
        return new Intl.DateTimeFormat(locale, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: timezone,
        }).format(dateObj)

      default:
        return dateObj.toLocaleDateString(locale)
    }
  } catch (error) {
    console.error('Date formatting error:', error)
    return dateObj.toString()
  }
}

/**
 * Format currency according to user preferences
 */
export const formatCurrency = (
  amount: number,
  options?: {
    currency?: string
    locale?: string
    showSymbol?: boolean
    precision?: number
  }
): string => {
  const { currency: defaultCurrency, language } = useSettingsStore.getState()
  const {
    currency = defaultCurrency,
    locale = language,
    showSymbol = true,
    precision = config.formats.currency.precision,
  } = options || {}

  const resolvedCurrency =
    typeof currency === 'string' && currency.trim().length === 3
      ? currency.trim().toUpperCase()
      : config.formats.currency.default || 'SAR'

  // Handle invalid numbers
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0'
  }

  try {
    if (showSymbol) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: resolvedCurrency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }).format(amount)
    }

    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(amount)
  } catch (error) {
    console.error('Currency formatting error:', error)
    // Fallback with Saudi Riyal symbol
    return `${amount.toFixed(precision)}`
  }
}

/**
 * Format number with thousands separators
 */
export const formatNumber = (
  value: number,
  options?: {
    locale?: string
    precision?: number
    compact?: boolean
  }
): string => {
  // Handle invalid values
  if (typeof value !== 'number' || isNaN(value)) {
    return '0'
  }

  const { language } = useSettingsStore.getState()
  const { locale = language, precision, compact = false } = options || {}

  try {
    const formatOptions: Intl.NumberFormatOptions = {}

    if (precision !== undefined) {
      formatOptions.minimumFractionDigits = precision
      formatOptions.maximumFractionDigits = precision
    }

    if (compact) {
      formatOptions.notation = 'compact'
      formatOptions.compactDisplay = 'short'
    }

    return new Intl.NumberFormat(locale, formatOptions).format(value)
  } catch (error) {
    console.error('Number formatting error:', error)
    return value.toString()
  }
}

/**
 * Format percentage
 */
export const formatPercentage = (
  value: number,
  options?: {
    locale?: string
    precision?: number
  }
): string => {
  if (typeof value !== 'number' || isNaN(value)) {
    return '0%'
  }

  const { language } = useSettingsStore.getState()
  const { locale = language, precision = 1 } = options || {}

  try {
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(value / 100)
  } catch (error) {
    console.error('Percentage formatting error:', error)
    return `${value.toFixed(precision)}%`
  }
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export const formatRelativeTime = (
  date: string | Date,
  options?: {
    locale?: string
  }
): string => {
  const { language } = useSettingsStore.getState()
  const { locale = language } = options || {}

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000)

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

    if (diffInSeconds < 60) {
      return rtf.format(-diffInSeconds, 'second')
    } else if (diffInSeconds < 3600) {
      return rtf.format(-Math.floor(diffInSeconds / 60), 'minute')
    } else if (diffInSeconds < 86400) {
      return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour')
    } else if (diffInSeconds < 2592000) {
      return rtf.format(-Math.floor(diffInSeconds / 86400), 'day')
    } else if (diffInSeconds < 31536000) {
      return rtf.format(-Math.floor(diffInSeconds / 2592000), 'month')
    } else {
      return rtf.format(-Math.floor(diffInSeconds / 31536000), 'year')
    }
  } catch (error) {
    console.error('Relative time formatting error:', error)
    return formatDate(dateObj, { format: 'short', locale })
  }
}

/**
 * Parse date string according to user's date format preference
 */
export const parseDate = (dateString: string): Date | null => {
  const { dateFormat } = useSettingsStore.getState()

  try {
    // Handle different date formats
    let parsed: Date

    switch (dateFormat) {
      case 'DD/MM/YYYY': {
        const [day, month, year] = dateString.split('/')
        parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
        break
      }
      case 'YYYY-MM-DD': {
        parsed = new Date(dateString)
        break
      }
      case 'MM/DD/YYYY':
      default: {
        const [month, day, year] = dateString.split('/')
        parsed = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
        break
      }
    }

    return isNaN(parsed.getTime()) ? null : parsed
  } catch (error) {
    console.error('Date parsing error:', error)
    return null
  }
}

/**
 * Format file size in human readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}
